import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { LandedCostBillingSchema } from "../landed-cost/landed-cost-buy-options";
import { OutboundShipmentTaxIdSchema } from "../outbound-labels/customs";
import {
  CanadaPostOption,
  RateProvider,
  rawOutboundRateSchema,
  ShipmentPaymentOptionSchema,
  ShipmentRefundStatus,
  SpecialRateEligibility,
} from "../outbound-labels/outbound-labels";
import { CustomParcelType } from "../outbound-labels/parcel";
import { HazmatSchema } from "./hazmat";

export enum TiktokShippingType {
  /** Tiktok provides shipping label */
  TIKTOK = "TIKTOK",
  /** Seller obtains shipping label and uploads tracking number to Tiktok */
  SELLER = "SELLER",
}

export enum TiktokShippingTypeTag {
  TIKTOK = "Shipping Type: Tiktok",
  SELLER = "Shipping Type: Seller",
}

export enum DhlEcommerceServiceEndorsement {
  AddressForwardingRequested = "1",
  ForwardingServiceRequested = "2",
  ChangeServiceRequested = "3",
  ReturnServiceRequested = "4",
}

export const DhlEcommerceServiceEndorsementSchema = createEnum(
  DhlEcommerceServiceEndorsement,
  { enumName: "DhlEcommerceServiceEndorsement" },
);

const dhlEcommerceLabelOptionsSchema = z.object({
  customLabelText1: z.string().nullish(),
  customLabelText2: z.string().nullish(),
  serviceEndorsement: DhlEcommerceServiceEndorsementSchema.nullish(),
});
export const DhlEcommerceLabelOptionsSchema: z.ZodType<DhlEcommerceLabelOptions> =
  dhlEcommerceLabelOptionsSchema;
export type DhlEcommerceLabelOptions = z.infer<
  typeof dhlEcommerceLabelOptionsSchema
>;

// DHL partnership certification teams. These teams authenticate against DHL's
// certification environment (DHL_ECOMMERCE_CERTIFICATION_* credentials) and get
// access to the certification label options (custom label text, ancillary
// endorsement, weight-based direct return labels).
export const DHL_CERTIFICATION_TEAM_IDS = new Set<string>([
  "6a1db6a84ef1324b6e44d52b", // production DHL certification store
  "6a1dc49f3b6b1095b893011d", // local DHL test instance
  "69efe038e4f1a01e6b84d8a7", // Coleman's DHL direct-return team
]);

export const isDhlCertificationTeam = (
  teamId: string | null | undefined,
): boolean => !!teamId && DHL_CERTIFICATION_TEAM_IDS.has(teamId);

const outboundShipmentAddressSchema = z.object({
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string(),
  country: z.string(),
  residential: z.boolean().nullish(),
  name: z.string().nullish(),
  company: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
});

export type OutboundShipmentAddress = z.infer<
  typeof outboundShipmentAddressSchema
>;

export const outboundShipmentAddressesEqual = (
  a: OutboundShipmentAddress,
  b: OutboundShipmentAddress,
) => {
  for (const key of Object.keys(a)) {
    if (
      a[key as keyof OutboundShipmentAddress] !==
      b[key as keyof OutboundShipmentAddress]
    ) {
      return false;
    }
  }
  return true;
};

const OutboundShipmentCustomsItemSchema = z.object({
  object: z.string(),
  description: z.string(),
  quantity: z.number(),
  origin_country: z.string(),
  hs_tariff_number: z.string(),
  // This is total value
  value: z.string(),
  unit_value: z.string().nullish(),
  weight: z.number(),
  code: z.string().nullish(),
  manufacturer: z.string().nullish(),
  product_identifier: z.string().nullish(),
});

export type OutboundShipmentCustomsItem = z.infer<
  typeof OutboundShipmentCustomsItemSchema
>;

export const OutboundShipmentCustomsInfoSchema = z.object({
  customs_info: z
    .object({
      object: z.string(),
      contents_type: z.string(),
      contents_explanation: z.string().nullish(),
      additional_customs_comments: z.string().nullish(),
      customs_certify: z.boolean(),
      customs_signer: z.string(),
      eel_pfc: z.string(),
      non_delivery_option: z.string(),
      restriction_type: z.literal("none"),
      customs_items: z.array(OutboundShipmentCustomsItemSchema),
      declaration: z.string().nullish(),
    })
    .nullish(),
});

export type OutboundShipmentCustomsInfo = z.infer<
  typeof OutboundShipmentCustomsInfoSchema
>;

export enum OutboundShipmentDeliveryConfirmation {
  None = "NO_SIGNATURE",
  Signature = "SIGNATURE",
  Adult_Signature = "ADULT_SIGNATURE",
}

// who pays the taxes and duties on international shipments
// Setting this value to anything other than "DDP" will pass the cost and responsibility of duties on to the recipient of the package
export enum OutboundShipmentIncoterm {
  CFR = "CFR",
  CIF = "CIF",
  CIP = "CIP",
  CPT = "CPT",
  DAT = "DAT",
  DAP = "DAP",
  DDP = "DDP",
  EXW = "EXW",
  FAS = "FAS",
  FCA = "FCA",
  FOB = "FOB",
}

const OutboundShipmentParcelSchema = z.object({
  length: z.number().nullish(), // inches
  width: z.number().nullish(),
  height: z.number().nullish(),
  weight: z.number(), // Ounces
  predefined_package: z.string().nullish(),
  parcelType: createEnum(CustomParcelType, {
    enumName: "CustomParcelType",
  }).nullish(),
  additionalHandlingIndicator: z.boolean().nullish(),
  largePackageIndicator: z.boolean().nullish(),
  shipperReleaseIndicator: z.boolean().nullish(),
  customs_info: OutboundShipmentCustomsInfoSchema.nullish(),
});

export type OutboundShipmentParcel = z.infer<
  typeof OutboundShipmentParcelSchema
>;

export const OutboundShipmentParcelKeys = Object.keys(
  OutboundShipmentParcelSchema.shape,
) as (keyof OutboundShipmentParcel)[];

const OutboundShipmentCustomPrintFieldsSchema = z.object({
  invoice_numbers: z.array(z.string()), // The sales order number. Should probably be called that
  po_numbers: z.array(z.string()).nullish(), // Purchase order number
  skus: z.array(z.object({ sku: z.string(), quantity: z.number() })),
  customer_ids: z.array(z.string()),
  custom_metafield_values: z.array(z.string()).nullish(),
  // If an order spans multiple packages, which package is this
  package_info: z
    .object({ package_number: z.number(), total_packages: z.number() })
    .nullish(),
});

export type OutboundShipmentCustomPrintFields = z.infer<
  typeof OutboundShipmentCustomPrintFieldsSchema
>;

export const OutboundShipmentRequestSchema = z.object({
  teamId: z.string(),
  from_address: outboundShipmentAddressSchema,
  to_address: outboundShipmentAddressSchema,
  return_address: outboundShipmentAddressSchema.nullish(),
  parcels: z.array(OutboundShipmentParcelSchema),
  is_return: z.boolean().nullish(),
  skip_one_rate: z.boolean().nullish(),
  delivery_confirmation: createEnum(OutboundShipmentDeliveryConfirmation, {
    enumName: "OutboundShipmentDeliveryConfirmation",
  }).nullish(),
  special_rates_eligibility: createEnum(SpecialRateEligibility, {
    enumName: "SpecialRateEligibility",
  }).nullish(),
  label_date: z.date().nullish(),
  tax_identifiers: z.array(OutboundShipmentTaxIdSchema).nullish(),
  incoterm: createEnum(OutboundShipmentIncoterm, {
    enumName: "OutboundShipmentIncoterm",
  }).nullish(),
  hazmat: HazmatSchema.nullish(),
  saturday_delivery: z.boolean().nullish(),
  customer_account_ids: z.array(z.string()).nullish(), // Unused, left only for backwards compatibility
  suppressCarrierCommercialInvoice: z.boolean().nullish(),
  dry_ice: z.boolean().nullish(),
  dry_ice_weight: z.string().nullish(),
  merchant_id: z.string().nullish(),
  carrier_accounts: z.array(z.string()).nullish(),
  payment: ShipmentPaymentOptionSchema.nullish(),
  blocked_providers: z
    .array(createEnum(RateProvider, { enumName: "RateProvider" }))
    .nullish(),
  carbon_neutral: z.boolean().nullish(),
  canada_post_option: createEnum(CanadaPostOption, {
    enumName: "CanadaPostOption",
  }).nullish(),
  direct_delivery_only: z.boolean().nullish(),
  custom_print_fields: OutboundShipmentCustomPrintFieldsSchema.nullish(),
  providerInfo: z
    .object({
      easypost: z.object({ reference: z.string() }).nullish(),
      soapBox: z
        .object({
          batchId: z.string(),
          orderId: z.string(),
          customBoxId: z.string(),
          packageId: z.number(),
        })
        .nullish(),
      ups: z.object({ shipperId: z.string().nullish() }).nullish(),
      landmarkGlobal: z
        .object({
          totalShippingCost: z.string().nullish(),
          customerEmail: z.string().nullish(),
          customerPhone: z.string().nullish(),
        })
        .nullish(),
      fedex: z
        .object({
          accountNumber: z.string().nullish(),
          smartPostHubId: z.string().nullish(),
        })
        .nullish(),
      dhlEcommerce: z
        .object({
          pickupId: z.string(),
          distributionCenter: z.string(),
          packageId: z.string().nullish(),
          customLabelText1Override: z.string().nullish(),
          customLabelText2Override: z.string().nullish(),
          serviceEndorsement: DhlEcommerceServiceEndorsementSchema.nullish(),
        })
        .nullish(),
      globale: z
        .object({
          globalEOrderId: z.string().nullish(),
          lineItems: z
            .array(z.object({ sku: z.string(), quantity: z.number() }))
            .nullish(),
        })
        .nullish(),
      tiktok: z
        .object({
          shippingType: createEnum(TiktokShippingType, {
            enumName: "TiktokShippingType",
          }),
          tiktokOrderIds: z.array(z.string()),
          tiktokIntegrationId: z.string(),
        })
        .nullish(),
      uniUni: z
        .object({ pickupWarehouseId: z.number(), customerId: z.string() })
        .nullish(),
    })
    .nullish(),
  duty_payment: LandedCostBillingSchema.nullish(),
});

export type CreateOutboundShipmentRequest = z.infer<
  typeof OutboundShipmentRequestSchema
>;

export const OutboundShipmentRequestKeys = Object.keys(
  OutboundShipmentRequestSchema.shape,
) as (keyof CreateOutboundShipmentRequest)[];

export enum OutboundShipmentPurchaseStatus {
  PURCHASED = "purchased",
  FAILED = "failed",
}

export const OutboundShipmentSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  createdAt: z.date(),
  updatedAt: z.date(),
  redoShipmentId: z.string(),
  providerBoughtShipmentId: z.string().nullish(),
  trackingNumber: z.string().nullish(),
  purchaseStatus: createEnum(OutboundShipmentPurchaseStatus, {
    enumName: "OutboundShipmentPurchaseStatus",
  }).nullish(),
  refundStatus: createEnum(ShipmentRefundStatus, {
    enumName: "ShipmentRefundStatus",
  }).nullish(),
  createOutboundShipmentRequest: OutboundShipmentRequestSchema,
  provider: createEnum(RateProvider, { enumName: "RateProvider" }).nullish(),
  rates: z.array(rawOutboundRateSchema),
  // Raw provider data for debugging/auditing - stored but excluded from reads
  rawProviderRatingRequests: z.array(z.any()).nullish(),
  rawProviderRatingResponses: z.array(z.any()).nullish(),
  rawProviderPurchaseRequest: z.any().nullish(),
  rawProviderPurchaseResponse: z.any().nullish(),
  rawProviderVoidResponse: z.any().nullish(),
  rawVoidResponse: z.any().nullish(),
  cleanedAt: z.date().nullish(),
});

export type OutboundShipment = z.infer<typeof OutboundShipmentSchema>;

export function joinOrderNames(
  orderNames: CreateOutboundShipmentRequest["custom_print_fields"],
) {
  if (!orderNames) {
    return "";
  }
  const uniqueOrderNames = new Set<string>(orderNames.invoice_numbers);
  return Array.from(uniqueOrderNames).join(", ");
}

export function joinPoNumbers(
  customPrintFields: CreateOutboundShipmentRequest["custom_print_fields"],
) {
  if (!customPrintFields?.po_numbers) {
    return "";
  }
  const uniquePoNumbers = new Set<string>(customPrintFields.po_numbers);
  return Array.from(uniquePoNumbers).join(", ");
}

export function joinOrderAndPoNumbers(
  customPrintFields: CreateOutboundShipmentRequest["custom_print_fields"],
) {
  const orderNames = joinOrderNames(customPrintFields);
  const poNumbers = joinPoNumbers(customPrintFields);
  if (orderNames && poNumbers) {
    return `${orderNames} - ${poNumbers}`;
  }
  return orderNames || poNumbers;
}

export function buildSkuList(
  skus: OutboundShipmentCustomPrintFields["skus"],
): string[] {
  return skus.map((sku) => `${sku.sku} x${sku.quantity}`);
}

export function isPoBoxAddress({
  line1,
  line2,
}: {
  line1?: string | null;
  line2?: string | null;
}): boolean {
  const normalizedStreet1 = line1?.toLowerCase() || "";
  const normalizedStreet2 = line2?.toLowerCase() || "";
  // Not matching bare "po"/"p.o."/"p.o"/"po." for fear of false positives
  // (e.g. "Portland", "Depot St"). If more robust detection is needed, use a
  // regex anchored to a following number, e.g. /\bp\.?\s*o\.?\s*(box\s*)?#?\s*\d+/i.
  const poBoxVariants = ["po box", "p.o. box", "p.o box", "po. box"];
  if (
    poBoxVariants.some(
      (variant) =>
        normalizedStreet1.includes(variant) ||
        normalizedStreet2.includes(variant),
    )
  ) {
    return true;
  }
  return false;
}

export function formatReference(input: string, maxLength: number): string {
  // Keep only letters, numbers, commas, dashes, and # signs. Don't go over the max length.
  return input.replace(/[^a-zA-Z0-9,#-]/g, " ").slice(0, maxLength);
}
